import React, { useState, useEffect } from 'react';

interface VendorMetrics {
  vendorId: string;
  vendorName: string;
  subscriptionTier: 'basic' | 'professional' | 'enterprise';
  performance: {
    revenue: {
      current: number;
      previous: number;
      growth: number;
      trend: 'up' | 'down' | 'stable';
    };
    products: {
      total: number;
      active: number;
      avgRating: number;
      totalViews: number;
      conversionRate: number;
    };
    reviews: {
      total: number;
      avgRating: number;
      responseRate: number;
      avgResponseTime: number; // hours
      trustedTesterReviews: number;
    };
    engagement: {
      profileViews: number;
      searchAppearances: number;
      clickThroughRate: number;
      bounceRate: number;
    };
    compliance: {
      labResultsCurrent: boolean;
      qualityScore: number;
      transparencyScore: number;
      customerServiceScore: number;
    };
  };
  insights: VendorInsight[];
  recommendations: VendorRecommendation[];
  benchmarks: {
    revenuePercentile: number;
    ratingPercentile: number;
    responseTimePercentile: number;
    compliancePercentile: number;
  };
}

interface VendorInsight {
  id: string;
  type: 'opportunity' | 'warning' | 'success' | 'trend';
  category: 'revenue' | 'products' | 'reviews' | 'compliance' | 'engagement';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  actionable: boolean;
  estimatedBenefit?: string;
}

interface VendorRecommendation {
  id: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
  title: string;
  description: string;
  steps: string[];
  estimatedImpact: string;
  estimatedEffort: 'low' | 'medium' | 'high';
}

interface ProductPerformance {
  id: string;
  name: string;
  category: string;
  strain: string;
  views: number;
  orders: number;
  revenue: number;
  rating: number;
  reviewCount: number;
  conversionRate: number;
  trend: 'up' | 'down' | 'stable';
  lastUpdated: string;
}

interface VendorPerformanceAnalyticsProps {
  vendorId: string;
  timeframe: '7d' | '30d' | '90d' | '1y';
  isOwnVendor?: boolean;
}

export default function VendorPerformanceAnalytics({ vendorId, timeframe = '30d', isOwnVendor = false }: VendorPerformanceAnalyticsProps) {
  const [vendorMetrics, setVendorMetrics] = useState<VendorMetrics | null>(null);
  const [productPerformance, setProductPerformance] = useState<ProductPerformance[]>([]);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'products' | 'reviews' | 'insights' | 'recommendations'>('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVendorAnalytics = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockInsights: VendorInsight[] = [
          {
            id: '1',
            type: 'opportunity',
            category: 'revenue',
            title: 'Premium Tier Upgrade Recommended',
            description: 'Your current usage exceeds Basic tier limits. Upgrading to Professional would increase visibility by 200%.',
            impact: 'high',
            actionable: true,
            estimatedBenefit: '+$2,400/month revenue'
          },
          {
            id: '2',
            type: 'warning',
            category: 'reviews',
            title: 'Response Time Above Average',
            description: 'Your 18-hour average response time is 3x the platform average. Faster responses improve ratings.',
            impact: 'medium',
            actionable: true,
            estimatedBenefit: '+0.3 rating points'
          },
          {
            id: '3',
            type: 'success',
            category: 'compliance',
            title: 'Excellent Lab Result Compliance',
            description: 'All your products have current lab results. This puts you in the top 10% of vendors.',
            impact: 'high',
            actionable: false
          },
          {
            id: '4',
            type: 'trend',
            category: 'products',
            title: 'Red Strain Demand Increasing',
            description: 'Red strain searches up 35% this month. Your red products are getting 2x more views.',
            impact: 'medium',
            actionable: true,
            estimatedBenefit: '+15% red strain sales'
          }
        ];

        const mockRecommendations: VendorRecommendation[] = [
          {
            id: '1',
            priority: 'high',
            category: 'Revenue Optimization',
            title: 'Upgrade to Professional Tier',
            description: 'Your current metrics suggest you would benefit significantly from Professional tier features.',
            steps: [
              'Review Professional tier benefits',
              'Calculate ROI based on current performance',
              'Upgrade subscription',
              'Optimize product listings for premium placement'
            ],
            estimatedImpact: '+200% visibility, +$2,400/month',
            estimatedEffort: 'low'
          },
          {
            id: '2',
            priority: 'high',
            category: 'Customer Service',
            title: 'Improve Response Time',
            description: 'Faster response times correlate with higher ratings and more sales.',
            steps: [
              'Set up email notifications for new reviews',
              'Create response templates for common questions',
              'Aim for <6 hour response time',
              'Monitor response metrics weekly'
            ],
            estimatedImpact: '+0.3 rating points, +12% sales',
            estimatedEffort: 'medium'
          },
          {
            id: '3',
            priority: 'medium',
            category: 'Product Strategy',
            title: 'Expand Red Strain Inventory',
            description: 'Red strain demand is surging. Consider adding more red varieties to your catalog.',
            steps: [
              'Analyze current red strain performance',
              'Research popular red strains you don\'t carry',
              'Source high-quality red strains',
              'Create compelling product descriptions',
              'Request Trusted Tester reviews for new products'
            ],
            estimatedImpact: '+15% revenue from red strains',
            estimatedEffort: 'high'
          }
        ];

        const mockProducts: ProductPerformance[] = [
          {
            id: '1',
            name: 'Premium Red Maeng Da',
            category: 'Powder',
            strain: 'Red Maeng Da',
            views: 3420,
            orders: 234,
            revenue: 8760,
            rating: 4.8,
            reviewCount: 47,
            conversionRate: 6.8,
            trend: 'up',
            lastUpdated: '2024-01-20'
          },
          {
            id: '2',
            name: 'Green Thai Kratom',
            category: 'Powder',
            strain: 'Green Thai',
            views: 2890,
            orders: 198,
            revenue: 5940,
            rating: 4.6,
            reviewCount: 32,
            conversionRate: 6.9,
            trend: 'stable',
            lastUpdated: '2024-01-19'
          },
          {
            id: '3',
            name: 'White Borneo Extract',
            category: 'Extract',
            strain: 'White Borneo',
            views: 1650,
            orders: 89,
            revenue: 4450,
            rating: 4.7,
            reviewCount: 18,
            conversionRate: 5.4,
            trend: 'up',
            lastUpdated: '2024-01-18'
          },
          {
            id: '4',
            name: 'Red Bali Superior',
            category: 'Powder',
            strain: 'Red Bali',
            views: 2340,
            orders: 156,
            revenue: 4680,
            rating: 4.5,
            reviewCount: 28,
            conversionRate: 6.7,
            trend: 'down',
            lastUpdated: '2024-01-17'
          }
        ];

        const mockMetrics: VendorMetrics = {
          vendorId: vendorId,
          vendorName: 'Premium Kratom Co',
          subscriptionTier: 'basic',
          performance: {
            revenue: {
              current: 28450,
              previous: 21200,
              growth: 34.2,
              trend: 'up'
            },
            products: {
              total: 24,
              active: 22,
              avgRating: 4.6,
              totalViews: 15420,
              conversionRate: 6.3
            },
            reviews: {
              total: 187,
              avgRating: 4.6,
              responseRate: 78,
              avgResponseTime: 18,
              trustedTesterReviews: 23
            },
            engagement: {
              profileViews: 2340,
              searchAppearances: 8750,
              clickThroughRate: 3.2,
              bounceRate: 24.5
            },
            compliance: {
              labResultsCurrent: true,
              qualityScore: 92,
              transparencyScore: 88,
              customerServiceScore: 74
            }
          },
          insights: mockInsights,
          recommendations: mockRecommendations,
          benchmarks: {
            revenuePercentile: 78,
            ratingPercentile: 85,
            responseTimePercentile: 34,
            compliancePercentile: 91
          }
        };

        setVendorMetrics(mockMetrics);
        setProductPerformance(mockProducts);
        setLoading(false);
      }, 1200);
    };

    fetchVendorAnalytics();
  }, [vendorId, timeframe]);

  const getPercentileColor = (percentile: number) => {
    if (percentile >= 80) return '#10b981';
    if (percentile >= 60) return '#f59e0b';
    if (percentile >= 40) return '#f97316';
    return '#ef4444';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      case 'stable': return '➡️';
      default: return '📊';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return '#10b981';
      case 'down': return '#ef4444';
      case 'stable': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return '💡';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'trend': return '📊';
      default: return '📋';
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'opportunity': return '#3b82f6';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      case 'trend': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading vendor analytics...</p>
      </div>
    );
  }

  if (!vendorMetrics) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Vendor analytics not available</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          Vendor Performance Analytics
        </h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ color: '#6b7280', fontSize: '1.125rem', margin: '0' }}>
            {vendorMetrics.vendorName} - {timeframe} Performance Overview
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ 
              padding: '0.5rem 1rem', 
              backgroundColor: '#3b82f620',
              color: '#3b82f6',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              textTransform: 'capitalize'
            }}>
              {vendorMetrics.subscriptionTier} Tier
            </span>
            {isOwnVendor && vendorMetrics.subscriptionTier === 'basic' && (
              <button style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}>
                Upgrade Plan
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Performance Overview Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '3rem' 
      }}>
        {/* Revenue Card */}
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', margin: '0 0 0.5rem 0', textTransform: 'uppercase' }}>
                Revenue ({timeframe})
              </h3>
              <div style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#e2e8f0' }}>
                ${vendorMetrics.performance.revenue.current.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>
                {getTrendIcon(vendorMetrics.performance.revenue.trend)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {vendorMetrics.benchmarks.revenuePercentile}th percentile
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: getTrendColor(vendorMetrics.performance.revenue.trend)
            }}>
              +{vendorMetrics.performance.revenue.growth.toFixed(1)}%
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              vs previous period
            </span>
          </div>
        </div>

        {/* Products Card */}
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', margin: '0 0 0.5rem 0', textTransform: 'uppercase' }}>
              Product Performance
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e2e8f0' }}>
                {vendorMetrics.performance.products.active}/{vendorMetrics.performance.products.total}
              </div>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Active Products</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
            <div>
              <div style={{ color: '#6b7280' }}>Avg Rating:</div>
              <div style={{ fontWeight: '600', color: '#e2e8f0' }}>★ {vendorMetrics.performance.products.avgRating}</div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Conversion:</div>
              <div style={{ fontWeight: '600', color: '#e2e8f0' }}>{vendorMetrics.performance.products.conversionRate}%</div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Total Views:</div>
              <div style={{ fontWeight: '600', color: '#e2e8f0' }}>{vendorMetrics.performance.products.totalViews.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Reviews Card */}
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', margin: '0 0 0.5rem 0', textTransform: 'uppercase' }}>
              Review Management
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e2e8f0' }}>
                {vendorMetrics.performance.reviews.total}
              </div>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Reviews</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
            <div>
              <div style={{ color: '#6b7280' }}>Response Rate:</div>
              <div style={{ fontWeight: '600', color: vendorMetrics.performance.reviews.responseRate >= 80 ? '#10b981' : '#f59e0b' }}>
                {vendorMetrics.performance.reviews.responseRate}%
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Avg Response:</div>
              <div style={{ fontWeight: '600', color: vendorMetrics.performance.reviews.avgResponseTime <= 12 ? '#10b981' : '#f59e0b' }}>
                {vendorMetrics.performance.reviews.avgResponseTime}h
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Trusted Testers:</div>
              <div style={{ fontWeight: '600', color: '#3b82f6' }}>{vendorMetrics.performance.reviews.trustedTesterReviews}</div>
            </div>
          </div>
        </div>

        {/* Compliance Card */}
        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
          border: '1px solid #4a5568'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', margin: '0 0 0.5rem 0', textTransform: 'uppercase' }}>
              Compliance Score
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e2e8f0' }}>
                {Math.round((vendorMetrics.performance.compliance.qualityScore + vendorMetrics.performance.compliance.transparencyScore + vendorMetrics.performance.compliance.customerServiceScore) / 3)}
              </div>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Overall Score</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
            <div>
              <div style={{ color: '#6b7280' }}>Quality:</div>
              <div style={{ fontWeight: '600', color: getPercentileColor(vendorMetrics.performance.compliance.qualityScore) }}>
                {vendorMetrics.performance.compliance.qualityScore}
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Transparency:</div>
              <div style={{ fontWeight: '600', color: getPercentileColor(vendorMetrics.performance.compliance.transparencyScore) }}>
                {vendorMetrics.performance.compliance.transparencyScore}
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Service:</div>
              <div style={{ fontWeight: '600', color: getPercentileColor(vendorMetrics.performance.compliance.customerServiceScore) }}>
                {vendorMetrics.performance.compliance.customerServiceScore}
              </div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Lab Results:</div>
              <div style={{ fontWeight: '600', color: vendorMetrics.performance.compliance.labResultsCurrent ? '#10b981' : '#ef4444' }}>
                {vendorMetrics.performance.compliance.labResultsCurrent ? '✅ Current' : '❌ Expired'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '2rem', overflowX: 'auto' }}>
            {(['overview', 'products', 'reviews', 'insights', 'recommendations'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                style={{
                  padding: '1rem 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: selectedTab === tab ? '3px solid #3b82f6' : '3px solid transparent',
                  color: selectedTab === tab ? '#3b82f6' : '#6b7280',
                  fontSize: '1rem',
                  fontWeight: selectedTab === tab ? '600' : '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {selectedTab === 'products' && (
        <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#e2e8f0' }}>
            Product Performance Analysis
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Product</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Views</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Orders</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Revenue</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Conversion</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Rating</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#a0aec0' }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {productPerformance.map(product => (
                  <tr key={product.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '1rem 0.75rem' }}>
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.25rem' }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {product.category} • {product.strain}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right', fontSize: '0.875rem', color: '#6b7280' }}>
                      {product.views.toLocaleString()}
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
                      {product.orders}
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                      ${product.revenue.toLocaleString()}
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right', fontSize: '0.875rem', color: '#e2e8f0' }}>
                      {product.conversionRate}%
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                        <span style={{ color: '#f59e0b' }}>★</span>
                        <span style={{ fontSize: '0.875rem', color: '#e2e8f0' }}>{product.rating}</span>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>({product.reviewCount})</span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem 0.75rem', textAlign: 'center' }}>
                      <span style={{ fontSize: '1.25rem' }}>{getTrendIcon(product.trend)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedTab === 'insights' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {vendorMetrics.insights.map(insight => (
            <div key={insight.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              borderLeft: `6px solid ${getInsightColor(insight.type)}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '2rem' }}>{getInsightIcon(insight.type)}</span>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0 0 0.25rem 0' }}>
                      {insight.title}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem',
                        backgroundColor: getInsightColor(insight.type) + '20',
                        color: getInsightColor(insight.type),
                        borderRadius: '0.25rem',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        {insight.category}
                      </span>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.5rem',
                        backgroundColor: insight.impact === 'high' ? '#ef444420' : insight.impact === 'medium' ? '#f59e0b20' : '#10b98120',
                        color: insight.impact === 'high' ? '#ef4444' : insight.impact === 'medium' ? '#f59e0b' : '#10b981',
                        borderRadius: '0.25rem',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        {insight.impact} Impact
                      </span>
                    </div>
                  </div>
                </div>
                
                {insight.estimatedBenefit && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                      {insight.estimatedBenefit}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      Estimated Benefit
                    </div>
                  </div>
                )}
              </div>
              
              <p style={{ fontSize: '1rem', color: '#a0aec0', margin: '0', lineHeight: '1.6' }}>
                {insight.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {selectedTab === 'recommendations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {vendorMetrics.recommendations.map(rec => (
            <div key={rec.id} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '1px solid #4a5568'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#e2e8f0', margin: '0' }}>
                      {rec.title}
                    </h3>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: getPriorityColor(rec.priority) + '20',
                      color: getPriorityColor(rec.priority),
                      borderRadius: '0.25rem',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {rec.priority} Priority
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    {rec.category}
                  </div>
                  <p style={{ fontSize: '1rem', color: '#a0aec0', margin: '0', lineHeight: '1.6' }}>
                    {rec.description}
                  </p>
                </div>
                
                <div style={{ textAlign: 'right', minWidth: '200px' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981', marginBottom: '0.25rem' }}>
                    {rec.estimatedImpact}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Estimated Impact
                  </div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.25rem 0.5rem',
                    backgroundColor: rec.estimatedEffort === 'high' ? '#ef444420' : rec.estimatedEffort === 'medium' ? '#f59e0b20' : '#10b98120',
                    color: rec.estimatedEffort === 'high' ? '#ef4444' : rec.estimatedEffort === 'medium' ? '#f59e0b' : '#10b981',
                    borderRadius: '0.25rem',
                    textTransform: 'uppercase',
                    fontWeight: '600'
                  }}>
                    {rec.estimatedEffort} Effort
                  </div>
                </div>
              </div>
              
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.75rem' }}>
                  Implementation Steps:
                </h4>
                <ol style={{ margin: '0', paddingLeft: '1.5rem', color: '#a0aec0' }}>
                  {rec.steps.map((step, index) => (
                    <li key={index} style={{ marginBottom: '0.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}